<template>
	<EventLayout :event="event" :subtitle="subtitle">
		<template #actions>
			<template v-if="canCreate">
				<LabeledObject>
					<template #label>{{ trans('events.teams.members') }}</template>
					<IconNumberInput v-model="members" :min="2" :step="1" icon-class="fa-solid fa-users"/>
				</LabeledObject>
				<LabeledObject>
					<template #label>{{ trans('events.teams.threshold') }}</template>
					<IconNumberInput v-model="threshold" :min="1" :max="members" :step="1" icon-class="fa-solid fa-plus-minus"/>
				</LabeledObject>
				<PrimaryButton :color="Color.Green" @click.stop="generateNewTeams">
					<i class="fa-solid fa-clipboard-check mr-2"/>
					{{ trans('general.create') }}
				</PrimaryButton>
				<PrimaryButton v-if="teamCount > 0" :color="Color.Teal" @click.stop="insertTeams">
					<i class="fa-solid fa-check-double mr-2"/>
					{{ trans('general.confirm') }}
				</PrimaryButton>
			</template>
			<template v-else>
				<LabeledObject>
					<template #label>{{ trans('events.teams.scores.includes_general_standings') }}</template>
					<Switch v-model="includesGeneralStandings" :label="trans('events.teams.scores.includes_general_standings')"/>
				</LabeledObject>
				<PrimaryButton :color="Color.Green" @click.stop="sendScoreUpdate">
					<i class="fa-solid fa-envelope mr-2"/>
					{{ trans('general.send') }}
				</PrimaryButton>
			</template>
		</template>
		<slot/>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import {type Team} from '@/Types/Events/Teams/Team'
import EventLayout from '@/Layouts/EventLayout.vue'
import Switch from '@/Components/UI/Switches/Switch.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {showInfo, showSuccess, showValidationErrors} from '@/Helpers/general'
import IconNumberInput from '@/Components/UI/Inputs/IconNumberInput.vue'

const props = defineProps<{
	event:Event,
	subtitle:string,
	teamCount?:number,
	canCreate:boolean,
	teams?:Array<Team>,
}>()

const emit = defineEmits([
	'created',
])

const members:Ref<number> = ref(2)
const event:Ref<Event> = ref(props.event)
const threshold:Ref<number> = ref(1)
const working:Ref<boolean> = ref(false)
const canCreate:Ref<boolean> = ref(props.canCreate)
const teams:Ref<Array<Team>> = ref(props.teams ?? [])
const teamCount:Ref<number> = ref(props.teamCount ?? 0)
const includesGeneralStandings:Ref<boolean> = ref(false)

function generateNewTeams():void{
	if(canCreate.value && !working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('events.teams.create', event.value.id), {
			members: members.value,
			threshold: threshold.value,
		}).then(({data}:{data:Array<Team>}) => {
			teams.value = data
			teamCount.value = data.length
			emit('created', data)
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function insertTeams():void{
	if(canCreate.value && !working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('events.teams.store', event.value.id), {
			teams: teams.value,
		}).then(() => {
			canCreate.value = false
			showSuccess(trans('general.inserted'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function sendScoreUpdate():void{
	if(!canCreate.value && !working.value && confirm(trans('events.teams.scores.mail.update.confirm'))){
		working.value = true
		showInfo(trans('general.updating'))
		axios.post(route('events.teams.scores.send', event.value.id), {
			includes_general_standings: includesGeneralStandings.value,
		}).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>